/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { CommittedImage } from '../components/CustomizedImageList';
import { Image } from '../components/ImageEnvironmentSelectFormItems';
import { EnvironmentImage } from '../components/ImageList';
import { useSuspendedBackendaiClient } from '../hooks';
import { AttachmentsProps } from '@ant-design/x';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import Big from 'big.js';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { TFunction } from 'i18next';
import * as _ from 'lodash-es';

export const newLineToBrElement = (
  text: string,
  separatorRegExp = /(<br\s*\/?>|\n)/,
) => {
  return _.map(_.split(text, separatorRegExp), (line, index) => {
    return line.match(separatorRegExp) ? <br key={index} /> : line;
  });
};

export const baiSignedRequestWithPromise = <ResponseType = any,>({
  method,
  url,
  body = null,
  client,
}: {
  method: string;
  url: string;
  body?: any;
  client: any;
}): Promise<ResponseType> => {
  const request = client?.newSignedRequest(method, url, body, null);
  return client?._wrapWithPromise(request);
};

export const useBaiSignedRequestWithPromise = () => {
  const baiClient = useSuspendedBackendaiClient();
  return <ResponseType = any,>({
    method,
    url,
    body = null,
  }: {
    method: string;
    url: string;
    body?: any;
  }): Promise<ResponseType> =>
    baiSignedRequestWithPromise<ResponseType>({
      method,
      url,
      body,
      client: baiClient,
    });
};

/**
 * Change date of any type to human readable date time.
 *
 * @param {Date} d   - string or DateTime object to convert
 * @return {Date}   - Formatted date / time to be human-readable text.
 */
export const _humanReadableTime = (date: string) => {
  return new Date(date).toUTCString();
};

export const GBToBytes = (value = 0) => {
  const gigabyte = Math.pow(10, 9);
  return Math.round(gigabyte * value);
};

export const bytesToGB = (
  bytes: number,
  decimalPoint = 2,
  nullStr: string = '-',
) => {
  if (bytes === null || bytes === undefined) return nullStr;
  if (!bytes) return bytes;
  return (bytes / 10 ** 9).toFixed(decimalPoint);
};

export type InputSizeUnit = '' | 'k' | 'm' | 'g' | 't' | 'p' | 'e';
export type SizeUnit = InputSizeUnit;

/**
 * Converts a value with a unit to a different unit or automatically selects the most appropriate unit.
 *
 * @param inputValue - The input string value with or without a unit (e.g., '1024m', '2g', '500').
 * @param targetUnit - The target unit to convert to ('', 'k', 'm', 'g', 't', 'p', 'e') or 'auto' for automatic unit selection.
 * @param options - Additional conversion options.
 * @param options.fixed - Number of decimal places to keep in the result. Defaults to 2.
 * @param options.round - Whether to round the result (true) or floor it (false). Defaults to false.
 * @param options.base - The base for conversion: 1024 (binary) or 1000 (decimal). Defaults to 1024.
 *
 * @returns An object containing the conversion result:
 * - `number`: The converted value as a number
 * - `numberFixed`: The converted value as a string with fixed decimal places
 * - `unit`: The target unit used for conversion
 * - `value`: A formatted string combining the number and unit
 *
 * @throws Error if the input format is invalid or the unit is not recognized
 *
 * @example
 * // Convert 1.5g to mega
 * convertUnitValue('1.5g', 'm')
 * // => { number: 1536, numberFixed: "1536", unit: "m", value: "1536m" }
 *
 * @example
 * // Auto unit selection with custom precision
 * convertUnitValue('1048576', 'auto', { fixed: 1 })
 * // => { number: 1, numberFixed: "1", unit: "m", value: "1m" }
 */
export function convertUnitValue(
  inputValue: string | undefined,
  targetUnit: InputSizeUnit | 'auto',
  options?: {
    fixed?: number;
    round?: boolean;
    base?: 1024 | 1000;
  },
) {
  const { fixed = 2, round = false, base = 1024 } = options || {};
  if (inputValue === undefined) {
    return undefined;
  }
  // display units
  const sizes = ['', 'k', 'm', 'g', 't', 'p', 'e'];
  const [sizeValue, sizeUnit] = parseValueWithUnit(inputValue);
  const sizeIndex = sizeUnit ? sizes.indexOf(sizeUnit.toLowerCase()) : 0;
  if (sizeIndex === -1 || isNaN(sizeValue)) {
    throw new Error('Invalid size format,' + inputValue);
  }

  const bytes = sizeValue * Math.pow(base, sizeIndex);
  let targetIndex: number;

  if (targetUnit === 'auto') {
    // Auto unit selection logic
    targetIndex = Math.floor(Math.log(bytes) / Math.log(base));
    targetIndex = Math.min(Math.max(targetIndex, 0), sizes.length - 1);

    const tempBytes = bytes / Math.pow(base, targetIndex);
    if (tempBytes < 1 && targetIndex > 0) {
      targetIndex--;
    }
  } else {
    targetIndex = targetUnit ? sizes.indexOf(targetUnit.toLowerCase()) : 0;
  }

  const finalBytes = bytes / Math.pow(base, targetIndex);
  const numberFixed = round
    ? finalBytes.toFixed(fixed)
    : toFixedFloorWithoutTrailingZeros(finalBytes, fixed);

  const unit = sizes[targetIndex] as InputSizeUnit;

  return {
    number: finalBytes,
    numberFixed,
    unit,
    value: `${numberFixed}${unit}`,
  };
}

export const generateDisplayValues = <T extends number | Big>(
  convertedValue:
    | {
        number: T;
        numberFixed: string;
        unit: InputSizeUnit;
        value: string;
      }
    | undefined,
  {
    baseDisplayUnit,
    displayUnitSuffix,
  }: {
    baseDisplayUnit: string;
    displayUnitSuffix: string;
  },
) => {
  if (convertedValue === undefined) {
    return undefined;
  }
  const displayUnit = convertedValue.unit
    ? `${convertedValue.unit.toUpperCase()}${displayUnitSuffix}`
    : baseDisplayUnit;
  const displayValue = `${convertedValue.numberFixed} ${displayUnit}`;
  return {
    ...convertedValue,
    displayValue,
    displayUnit,
  };
};

/**
 * Converts a binary size value from one unit to another.
 *
 * @param inputValue - The value to convert, can be a string, number, or undefined
 * @param targetUnit - The unit to convert to, or 'auto' for automatic unit selection
 * @param fixed - The number of decimal places to fix the result to (default: 2)
 * @param round - Whether to round the result (default: false)
 * @returns An object containing the converted value information or undefined if conversion fails:
 *          - number: The converted number value
 *          - numberFixed: The formatted number with fixed decimal places
 *          - unit: The resulting unit (lowercase)
 *          - displayValue: Formatted string with value and unit (e.g., "10.24 KiB")
 *          - displayUnit: The formatted unit string (e.g., "KiB")
 */
export function convertToBinaryUnit(
  inputValue: string | number | undefined,
  targetUnit: InputSizeUnit | 'auto',
  fixed: number = 2,
  round: boolean = false,
) {
  inputValue = _.isNumber(inputValue) ? _.toString(inputValue) : inputValue;
  return generateDisplayValues(
    convertUnitValue(inputValue, targetUnit, {
      fixed,
      round,
      base: 1024,
    }),
    {
      baseDisplayUnit: 'BiB',
      displayUnitSuffix: 'iB',
    },
  );
}

/**
 * Converts a size value to a different unit in the decimal-based system.
 *
 * @param inputValue - The input size value to convert, can be a string, number or undefined
 * @param targetUnit - The desired unit to convert to, or 'auto' to determine the best unit automatically
 * @param fixed - The number of decimal places to round to, defaults to 2
 * @param round - Whether to round the value (true) or truncate (false), defaults to false
 * @returns An object containing the converted value information, including:
 *          - numberFixed: The converted number with fixed decimal places
 *          - displayValue: The formatted string with value and unit (e.g., "5.25 KB")
 *          - displayUnit: The display unit (e.g., "KB")
 *          - additional properties from convertUnitValue
 *          Returns undefined if the conversion failed
 */
export function convertToDecimalUnit(
  inputValue: string | number | undefined,
  targetUnit: InputSizeUnit | 'auto',
  fixed: number = 2,
  round: boolean = false,
) {
  inputValue = _.isNumber(inputValue) ? _.toString(inputValue) : inputValue;
  return generateDisplayValues(
    convertUnitValue(inputValue, targetUnit, {
      fixed,
      round,
      base: 1000,
    }),
    {
      baseDisplayUnit: 'B',
      displayUnitSuffix: 'B',
    },
  );
}

export function toFixedFloorWithoutTrailingZeros(
  num: number | string,
  fixed: number,
) {
  const number = typeof num === 'string' ? parseFloat(num) : num;
  return parseFloat(number.toFixed(fixed)).toString();
}

export function toFixedWithTypeValidation(num: number | string, fixed: number) {
  return typeof num === 'number'
    ? num.toFixed(fixed)
    : parseFloat(num).toFixed(fixed);
}

export function compareNumberWithUnits(
  size1: string | number,
  size2: string | number,
) {
  const [number1, unit1] = parseValueWithUnit(_.toString(size1));
  const [number2, unit2] = parseValueWithUnit(_.toString(size2));

  if (unit1 === unit2) {
    return number1 - number2;
  }
  if (number1 === 0 && number2 === 0) {
    return 0;
  }

  const value1 = convertToBinaryUnit(size1, 'g')?.number ?? 0;
  const value2 = convertToBinaryUnit(size2, 'g')?.number ?? 0;
  return value1 - value2;
}

export function addNumberWithUnits(
  size1: string,
  size2: string,
  targetUnit: InputSizeUnit = 'm',
) {
  return convertToBinaryUnit(
    (convertToBinaryUnit(size1, '')?.number || 0) +
      (convertToBinaryUnit(size2, '')?.number || 0),
    targetUnit,
  )?.value;
}

export function subNumberWithUnits(
  size1: string,
  size2: string,
  targetUnit: InputSizeUnit = 'm',
) {
  return convertToBinaryUnit(
    (convertToBinaryUnit(size1, '')?.number || 0) -
      (convertToBinaryUnit(size2, '')?.number || 0),
    targetUnit,
  )?.value;
}

export type QuotaScopeType = 'project' | 'user';
export const addQuotaScopeTypePrefix = (type: QuotaScopeType, str: string) => {
  if (str === '' || str === undefined) return '';
  if (str.startsWith(`${type}:`)) return str;
  return `${type}:${str}`;
};

export const usageIndicatorColor = (percentage: number | undefined) => {
  return percentage === undefined
    ? undefined
    : percentage < 70
      ? 'rgba(58, 178, 97, 1)'
      : percentage < 90
        ? 'rgb(223, 179, 23)'
        : '#ef5350';
};

export const maskString = (
  value = '',
  maskChar = '*',
  startFrom = 0,
  maskLength = 0,
) => {
  // clamp mask length
  maskLength =
    startFrom + maskLength > value.length ? value.length : maskLength;
  return (
    value.substring(0, startFrom) +
    maskChar.repeat(maskLength) +
    value.substring(startFrom + maskLength, value.length)
  );
};

export const offset_to_cursor = (offset: number): string => {
  return window.btoa(`arrayconnection:${offset}`);
};

export function parseValueWithUnit(str: string): [number, string | undefined] {
  const match = str?.match(/^(\d*\.?\d+)\s*([a-zA-Z%]*)$/);
  if (!match) {
    // If the input doesn't match the pattern, assume it's in bytes
    return [parseFloat(str), undefined];
  }
  const num = parseFloat(match[1]);
  const unit = match[2];
  return [num, unit];
}

export const isOutsideRange = (
  value?: number | string,
  min?: number | string,
  max?: number | string,
) => {
  if (value === undefined) return false;
  value = _.toNumber(value);
  if (min !== undefined && value < _.toNumber(min)) return true;
  if (max !== undefined && value > _.toNumber(max)) return true;
  return false;
};
export const isOutsideRangeWithUnits = (
  value: string,
  min?: string,
  max?: string,
) => {
  if (value === undefined) return false;
  if (min !== undefined && compareNumberWithUnits(value, min) < 0) return true;
  if (max !== undefined && compareNumberWithUnits(value, max) > 0) return true;
  return false;
};

export const getImageFullName = (
  image: DeepPartial<Image | CommittedImage | EnvironmentImage>,
) => {
  return image
    ? `${image.registry}/${image.namespace ?? image.name}:${image.tag}@${image.architecture}`
    : undefined;
};

/**
 * Parse an image string to extract its components.
 * Image string format: registry/namespace:tag@arch
 * - registry can include port (e.g., myregistry.org:5000)
 * - tag is the part after the last "/" and after ":"
 * - arch is after "@"
 *
 * @param imageString - The image string to parse
 * @returns Parsed components: registryAndNamespace, tag, architecture, and flags
 */
export const parseImageString = (
  imageString: string,
): {
  registryAndNamespace: string;
  tag: string | undefined;
  architecture: string | undefined;
  hasTag: boolean;
  hasArch: boolean;
} => {
  // Defensive validation for invalid input
  if (!imageString || typeof imageString !== 'string') {
    return {
      registryAndNamespace: '',
      tag: undefined,
      architecture: undefined,
      hasTag: false,
      hasArch: false,
    };
  }

  // Check for architecture (after @)
  const hasArch = imageString.includes('@');
  let architecture: string | undefined;
  let withoutArch = imageString;

  if (hasArch) {
    const atIndex = imageString.lastIndexOf('@');
    architecture = imageString.slice(atIndex + 1);
    withoutArch = imageString.slice(0, atIndex);
  }

  // Check for tag - must be after the last "/"
  // This handles registry with port like "myregistry.org:5000/namespace:tag"
  const lastSlashIndex = withoutArch.lastIndexOf('/');
  const afterLastSlash =
    lastSlashIndex >= 0 ? withoutArch.slice(lastSlashIndex) : withoutArch;
  const hasTag = afterLastSlash.includes(':');

  let registryAndNamespace: string;
  let tag: string | undefined;

  if (hasTag) {
    // Find the ":" after the last "/"
    const tagColonIndex =
      lastSlashIndex >= 0
        ? lastSlashIndex + afterLastSlash.indexOf(':')
        : afterLastSlash.indexOf(':');
    registryAndNamespace = withoutArch.slice(0, tagColonIndex);
    tag = withoutArch.slice(tagColonIndex + 1);
  } else {
    registryAndNamespace = withoutArch;
  }

  return {
    registryAndNamespace,
    tag,
    architecture,
    hasTag,
    hasArch,
  };
};

/**
 * Remove architecture suffix from an image full name.
 * @param fullName - The full image name (e.g., "registry/namespace:tag@arch")
 * @returns The image name without architecture (e.g., "registry/namespace:tag")
 */
export const removeArchitectureFromImageFullName = (
  fullName: string | undefined,
): string | undefined => {
  return fullName?.replace(/@[^@]+$/, '');
};

export const localeCompare = (a?: string | null, b?: string | null) => {
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  return a.localeCompare(b);
};

/**
 * Compare two dot-separated version strings numerically.
 * @returns 1 when version1 is newer, -1 when older, 0 when equal
 */
export const compareImageVersions = (
  version1: string,
  version2: string,
): number => {
  const v1 = version1.split('.').map(Number);
  const v2 = version2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = v1[i] || 0;
    const num2 = v2[i] || 0;

    if (num1 > num2) {
      return 1;
    } else if (num1 < num2) {
      return -1;
    }
  }

  return 0;
};

type PartialImageRef = NonNullable<
  DeepPartial<Image | CommittedImage | EnvironmentImage>
>;

/**
 * Whether an image is marked private via the `ai.backend.features` label.
 * The session launcher form (`ImageEnvironmentSelectFormItems`) hides private
 * images unless explicitly asked to show them.
 */
export const isPrivateImage = (
  image: PartialImageRef | null | undefined,
): boolean => {
  return _.some(image?.labels, (label) => {
    return (
      label?.key === 'ai.backend.features' &&
      label?.value?.split(' ').includes('private')
    );
  });
};

/**
 * Resolve a possibly-partial image reference from `config.toml` against the
 * registered image list, following the formats documented in
 * `config.toml.sample` for `defaultSessionEnvironment` /
 * `defaultImportEnvironment`:
 *
 * - `registry/namespace:tag@arch` → the exact image
 * - `registry/namespace:tag` → the first available architecture for that tag
 * - `registry/namespace` → the latest version, first available architecture
 * - `registry/namespace@arch` (undocumented) → the latest version restricted
 *   to that architecture
 *
 * This mirrors the matching `ImageEnvironmentSelectFormItems` performs for the
 * session launcher form, so launch paths that bypass that form (the Start from
 * URL import features) resolve the same config value to the same image.
 * Private images are excluded from the candidate set, matching the launcher
 * form's default behavior.
 *
 * @returns the full image name (`registry/namespace:tag@arch`), or `undefined`
 *   when no registered image matches
 */
export const resolveImageFullName = (
  imageString: string | undefined | null,
  images: ReadonlyArray<PartialImageRef | null | undefined> | undefined | null,
): string | undefined => {
  if (!imageString) return undefined;

  const candidates = (images ?? []).filter(
    (image): image is PartialImageRef => !!image && !isPrivateImage(image),
  );

  // 1. Exact full name match (registry/namespace:tag@arch)
  const exactMatch = _.find(
    candidates,
    (image) => getImageFullName(image) === imageString,
  );
  if (exactMatch) return getImageFullName(exactMatch);

  const { registryAndNamespace, architecture, hasTag, hasArch } =
    parseImageString(imageString);

  // 2. Tag but no architecture: take the first available architecture.
  // Sorted by architecture name so the pick does not depend on the order the
  // server happened to return the images in.
  if (hasTag && !hasArch) {
    const sameTag = _.filter(
      candidates,
      (image) =>
        removeArchitectureFromImageFullName(getImageFullName(image)) ===
        imageString,
    );
    const matched = _.first(
      [...sameTag].sort((a, b) =>
        localeCompare(a?.architecture, b?.architecture),
      ),
    );
    return matched ? getImageFullName(matched) : undefined;
  }

  // 3. No tag: take the latest version. An explicit `@arch` without a tag
  // (`registry/namespace@arch`) restricts the candidates to that architecture
  // instead of silently returning a different one.
  if (!hasTag) {
    const sameEnvironment = _.filter(
      candidates,
      (image) =>
        `${image.registry}/${image.namespace ?? image.name}` ===
          registryAndNamespace &&
        (!hasArch || image.architecture === architecture),
    );
    // Sorted the same way as the launcher's version select: latest first,
    // then by architecture name so the pick is deterministic.
    const latest = _.first(
      [...sameEnvironment].sort(
        (a, b) =>
          compareImageVersions(
            b?.tag?.split('-')?.[0] ?? '',
            a?.tag?.split('-')?.[0] ?? '',
          ) || localeCompare(a?.architecture, b?.architecture),
      ),
    );
    return latest ? getImageFullName(latest) : undefined;
  }

  return undefined;
};

export const numberSorterWithInfinityValue = (
  a?: number | null,
  b?: number | null,
  infiniteValue?: number | null,
  nullishFallbackValue: number = 0,
) => {
  const transform = (value?: number | null) => {
    if (value === infiniteValue) return Number.POSITIVE_INFINITY;
    if (value === null || value === undefined) return nullishFallbackValue;
    return value;
  };
  return transform(a) - transform(b);
};

export function formatToUUID(str: string) {
  if (str.length !== 32) {
    throw new Error('Input string must be 32 characters long');
  }

  return `${str.slice(0, 8)}-${str.slice(8, 12)}-${str.slice(12, 16)}-${str.slice(16, 20)}-${str.slice(20)}`;
}

export function preserveDotStartCase(str: string = '') {
  // Temporarily replace periods with a unique placeholder
  const placeholder = '<<<DOT>>>';
  const tempStr = str.replace(/\./g, placeholder);

  const startCased = _.startCase(tempStr);

  // Replace the placeholder back with periods
  return startCased.replace(new RegExp(placeholder, 'g'), '.');
}

export function formatDuration(duration: Duration, t: TFunction) {
  // We need to `t` function to translate the time unit in React render phase
  return [
    duration.weeks() ? `${duration.weeks()} ${t('time.Week')}` : '',
    duration.days() ? `${duration.days()} ${t('time.Day')}` : '',
    duration.hours() ? `${duration.hours()} ${t('time.Hour')}` : '',
    duration.minutes() ? `${duration.minutes()} ${t('time.Min')}` : '',
    duration.seconds() ? `${duration.seconds()} ${t('time.Sec')}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function formatDurationAsDays(
  start: string,
  end?: string | null,
  dayLabel: string = 'd ',
): string {
  const startTime = dayjs(start);
  const endTime = end ? dayjs(end) : dayjs();

  const diff = dayjs.duration(endTime.diff(startTime));

  const asDays = Math.floor(diff.asDays());
  const hours = diff.hours().toString().padStart(2, '0');
  const minutes = diff.minutes().toString().padStart(2, '0');
  const seconds = diff.seconds().toString().padStart(2, '0');

  return `${asDays ? `${asDays}${dayLabel}` : ''}${hours}:${minutes}:${seconds}`;
}

export const handleRowSelectionChange = <T extends object, K extends keyof T>(
  selectedRowKeys: React.Key[],
  currentPageItems: readonly T[],
  setSelectedItems: React.Dispatch<React.SetStateAction<T[]>>,
  keyField: K = 'id' as unknown as K,
) => {
  // Find items that are no longer selected on current page
  const deselectedItemsOnCurrentPage = currentPageItems.filter(
    (item) => !selectedRowKeys.includes(item[keyField] as React.Key),
  );

  // Find newly selected items on current page
  const selectedItemsOnCurrentPage = selectedRowKeys
    .map((key) => currentPageItems.find((item) => item[keyField] === key))
    .filter((item): item is T => item !== undefined);

  setSelectedItems((prevSelected) => {
    // Combine previous selection with new selections
    const combinedSelection = [...prevSelected, ...selectedItemsOnCurrentPage];

    // Remove duplicates and deselected items
    return _.uniqBy(combinedSelection, keyField as string).filter(
      (item) =>
        !_.some(
          deselectedItemsOnCurrentPage,
          (di) => di[keyField] === item[keyField],
        ),
    );
  });
};

export function createDataTransferFiles(files: AttachmentsProps['items']) {
  const fileList = _.map(files, (item) => item.originFileObj as File);
  const dataTransfer = new DataTransfer();
  _.forEach(fileList, (file) => {
    dataTransfer.items.add(file);
  });
  return dataTransfer.files;
}

export function getOS() {
  const userAgent = navigator.userAgent.toLowerCase();
  if (_.includes(userAgent, 'windows')) return 'Windows';
  if (_.includes(userAgent, 'macintosh')) return 'MacOS';
  if (_.includes(userAgent, 'linux')) return 'Linux';
  return 'Linux';
}

/**
 * Validates if a string is a valid IPv4 address
 *
 * Strict validation rules:
 * - No leading zeros (e.g., 01.2.3.4 is invalid)
 * - No whitespace
 * - Exactly 4 octets
 * - Each octet must be 0-255
 */
export function isValidIPv4(ip: string): boolean {
  // Quick format check
  if (!ip || ip.trim() !== ip) return false;

  const parts = ip.split('.');
  if (parts.length !== 4) return false;

  for (const part of parts) {
    // Empty octet
    if (part.length === 0) return false;

    // Leading zero check (except '0' itself)
    if (part.length > 1 && part[0] === '0') return false;

    // Must be numeric
    const num = Number(part);
    if (isNaN(num) || num < 0 || num > 255) return false;

    // Must not have non-numeric characters
    if (!/^\d+$/.test(part)) return false;
  }

  return true;
}

/**
 * Validates if a string is a valid IPv6 address
 *
 * Strict validation rules:
 * - No whitespace
 * - No zone identifiers (e.g., %eth0)
 * - No IPv4-mapped addresses (e.g., ::ffff:192.168.0.1)
 * - Exactly 8 groups or proper :: compression
 * - Each group must be 1-4 hex digits
 * - Case-insensitive hex digits allowed
 */
export function isValidIPv6(ip: string): boolean {
  // Quick format check
  if (!ip || ip.trim() !== ip) return false;

  // Reject zone identifiers
  if (ip.includes('%')) return false;

  // Reject IPv4-mapped addresses
  if (ip.includes('.')) return false;

  // Handle :: compression
  const doubleColonCount = (ip.match(/::/g) || []).length;
  if (doubleColonCount > 1) return false; // Only one :: allowed

  // Split by ::
  if (ip.includes('::')) {
    const [left, right] = ip.split('::');
    const leftGroups = left ? left.split(':') : [];
    const rightGroups = right ? right.split(':') : [];

    // Check if total groups <= 8
    if (leftGroups.length + rightGroups.length >= 8) return false;

    // Validate each group
    const allGroups = [...leftGroups, ...rightGroups];
    for (const group of allGroups) {
      if (group.length === 0) continue; // Empty group is ok in split result
      if (group.length > 4) return false;
      if (!/^[0-9a-fA-F]+$/.test(group)) return false;
    }

    return true;
  }

  // No :: compression - must have exactly 8 groups
  const groups = ip.split(':');
  if (groups.length !== 8) return false;

  for (const group of groups) {
    if (group.length === 0 || group.length > 4) return false;
    if (!/^[0-9a-fA-F]+$/.test(group)) return false;
  }

  return true;
}

/**
 * Validates if a string is a valid IP address (IPv4 or IPv6)
 */
export function isValidIP(ip: string): boolean {
  return isValidIPv4(ip) || isValidIPv6(ip);
}

/**
 * Validates if an IPv4 address matches its network prefix
 * @param ip - IPv4 address string
 * @param prefixLength - Number of network bits (0-32)
 * @returns true if the IP is a valid network address for the given prefix
 */
function isValidIPv4NetworkAddress(ip: string, prefixLength: number): boolean {
  const octets = ip.split('.').map(Number);

  // Convert IP to 32-bit unsigned integer
  let ipBits = 0;
  for (let i = 0; i < 4; i++) {
    ipBits = ((ipBits << 8) | octets[i]) >>> 0; // >>> 0 ensures unsigned
  }

  // Create network mask (unsigned)
  const mask = prefixLength === 0 ? 0 : (~0 << (32 - prefixLength)) >>> 0;

  // Check if host bits are all zero
  const networkAddress = (ipBits & mask) >>> 0;
  return ipBits === networkAddress;
}

/**
 * Validates if an IPv6 address matches its network prefix
 * @param ip - IPv6 address string
 * @param prefixLength - Number of network bits (0-128)
 * @returns true if the IP is a valid network address for the given prefix
 */
function isValidIPv6NetworkAddress(ip: string, prefixLength: number): boolean {
  // Expand IPv6 address to full form
  const expandIPv6 = (ip: string): string => {
    // Handle :: abbreviation
    if (ip.includes('::')) {
      const sides = ip.split('::');
      const leftGroups = sides[0] ? sides[0].split(':') : [];
      const rightGroups = sides[1] ? sides[1].split(':') : [];
      const missingGroups = 8 - leftGroups.length - rightGroups.length;
      const middleGroups = Array(missingGroups).fill('0000');
      return [...leftGroups, ...middleGroups, ...rightGroups]
        .map((g) => g.padStart(4, '0'))
        .join(':');
    }
    return ip
      .split(':')
      .map((g) => g.padStart(4, '0'))
      .join(':');
  };

  try {
    const expanded = expandIPv6(ip);
    const groups = expanded.split(':');

    if (groups.length !== 8) return false;

    // Convert to binary string
    let binaryStr = '';
    for (const group of groups) {
      const num = parseInt(group, 16);
      if (isNaN(num)) return false;
      binaryStr += num.toString(2).padStart(16, '0');
    }

    // Check if host bits (after prefix length) are all zero
    for (let i = prefixLength; i < 128; i++) {
      if (binaryStr[i] === '1') return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Validates if a string is a valid CIDR range (IPv4 or IPv6)
 * Examples: 10.20.30.0/24, 192.168.1.0/16, 2001:db8::/32
 *
 * This function validates:
 * 1. IP address format (IPv4 or IPv6)
 * 2. Prefix length is in valid range
 * 3. Host bits are zero (network address is correct)
 *
 * Invalid examples:
 * - 10.20.30.40/12 (host bits are non-zero, should be 10.16.0.0/12)
 * - 192.168.1.5/24 (host bits are non-zero, should be 192.168.1.0/24)
 */
export function isCidrRange(cidr: string): boolean {
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;

  const [ip, prefix] = parts;
  const prefixNum = parseInt(prefix, 10);

  if (isNaN(prefixNum)) return false;

  // IPv4 CIDR
  if (isValidIPv4(ip)) {
    if (prefixNum < 0 || prefixNum > 32) return false;
    return isValidIPv4NetworkAddress(ip, prefixNum);
  }

  // IPv6 CIDR
  if (isValidIPv6(ip)) {
    if (prefixNum < 0 || prefixNum > 128) return false;
    return isValidIPv6NetworkAddress(ip, prefixNum);
  }

  return false;
}

/**
 * Validates if a string is a valid IP address or CIDR range
 */
export function isValidIPOrCidr(value: string): boolean {
  return isValidIP(value) || isCidrRange(value);
}

/**
 * Checks if a given IP address is contained within an IP or CIDR range.
 * Supports both IPv4 addresses and CIDR notation.
 * - If entry is a plain IP, checks exact match.
 * - If entry is a CIDR range, checks if the IP falls within the range.
 */
export function isIpInRange(ip: string, entry: string): boolean {
  if (!isValidIPv4(ip)) return false;

  // Exact IP match
  if (isValidIPv4(entry)) {
    return ip === entry;
  }

  // CIDR range check
  if (!isCidrRange(entry)) return false;
  const [network, prefix] = entry.split('/');
  const prefixLength = parseInt(prefix, 10);

  const ipToInt = (addr: string): number => {
    const octets = addr.split('.').map(Number);
    let bits = 0;
    for (let i = 0; i < 4; i++) {
      bits = ((bits << 8) | octets[i]) >>> 0;
    }
    return bits;
  };

  const mask = prefixLength === 0 ? 0 : (~0 << (32 - prefixLength)) >>> 0;
  return (ipToInt(ip) & mask) >>> 0 === (ipToInt(network) & mask) >>> 0;
}

/**
 * Checks if a given IP address is included in any of the provided IP/CIDR entries.
 */
export function isIpIncludedInList(ip: string, entries: string[]): boolean {
  return entries.some((entry) => isIpInRange(ip, entry));
}

type SSEHandlerKeys =
  'onUpdated' | 'onDone' | 'onFailed' | 'onTaskFailed' | 'onTaskCancelled';

export type SSEEventHandlerTypes<
  BaseType = unknown,
  Overrides extends Partial<Record<SSEHandlerKeys, any>> = NonNullable<unknown>,
> = {
  [K in SSEHandlerKeys]: (
    data: K extends keyof Overrides ? Overrides[K] : BaseType,
    controller?: AbortController,
  ) => void;
};
/**
 * Listens to background task events using Server-Sent Events (SSE).
 * @param taskID
 * @param handlers
 * @returns
 */
export function listenToBackgroundTask<
  BaseType = unknown,
  Overrides extends Partial<Record<SSEHandlerKeys, any>> = NonNullable<unknown>,
>(
  taskID: string,
  handlers: Partial<SSEEventHandlerTypes<BaseType, Overrides>>,
): () => void {
  const controller = new AbortController();
  const signal = controller.signal;

  const searchParams = new URLSearchParams({ task_id: taskID });
  const reqUrl = `/events/background-task?${searchParams.toString()}`;

  // @ts-ignore
  const req = globalThis.backendaiclient?.newSignedRequest('GET', reqUrl, null);

  if (!req) {
    throw new Error('Failed to create request for background task events');
  }

  fetchEventSource(req.uri, {
    signal,
    credentials: 'include',
    openWhenHidden: true,
    headers: {
      'x-backendai-sessionid':
        localStorage.getItem('backendaiwebui.sessionid') || '',
    },
    onmessage: (event) => {
      const data = JSON.parse(event.data);

      switch (event.event) {
        case 'bgtask_updated':
          handlers.onUpdated?.(data, controller);
          break;
        case 'bgtask_done':
          handlers.onDone?.(data, controller);
          controller.abort();
          break;
        case 'bgtask_failed':
          handlers.onTaskFailed?.(data, controller);
          controller.abort();
          break;
        case 'task_failed':
          handlers.onFailed?.(data, controller);
          controller.abort();
          break;
        case 'bgtask_cancelled':
          handlers.onTaskCancelled?.(data, controller);
          controller.abort();
          break;
      }
    },
    onerror: (_error) => {
      controller.abort();
      // Don't throw error here. If you throw error, it will not retry automatically.
    },
  });

  return controller.abort.bind(controller);
}

/**
 * Converts an order string (e.g., 'name' or '-name') to a GraphQL v2 (Strawberry) OrderBy array.
 * If the order string contains commas (from array dataIndex like ['spec', 'weight']),
 * only the last part is used as the field name.
 *
 * @template TOrderBy - The type of the order by object (e.g., ResourceGroupOrderBy)
 * @param order - The order string. Prefix with '-' for descending order.
 * @param fieldNameMap - Optional mapping from client field names to server field names.
 * @returns An array containing a single OrderBy object, or undefined if order is null/undefined.
 *
 * @example
 * convertToOrderBy<ResourceGroupOrderBy>('name')
 * // => [{ field: 'NAME', direction: 'ASC' }]
 *
 * @example
 * convertToOrderBy<ResourceGroupOrderBy>('-name')
 * // => [{ field: 'NAME', direction: 'DESC' }]
 *
 * @example
 * // With field name mapping for server compatibility
 * convertToOrderBy<DomainFairShareOrderBy>(
 *   '-calculationSnapshot,fairShareFactor',
 *   { fairShareFactor: 'FAIR_SHARE_FACTOR' }
 * )
 * // => [{ field: 'FAIR_SHARE_FACTOR', direction: 'DESC' }]
 */
export const convertToOrderBy = <
  TOrderBy extends { field?: string; direction?: string },
>(
  order: string | null | undefined,
): ReadonlyArray<TOrderBy> | undefined => {
  if (!order) return undefined;

  // Check for descending order before splitting
  const isDescending = order.startsWith('-');
  const cleanOrder = isDescending ? order.slice(1) : order;

  // If order contains comma-separated values, extract the last one
  const orderParts = cleanOrder.split(',');
  const lastOrder = orderParts[orderParts.length - 1].trim();

  return [
    {
      field: _.snakeCase(lastOrder).toUpperCase(),
      direction: isDescending ? 'DESC' : 'ASC',
    } as TOrderBy,
  ];
};

/**
 * Reverses `convertToOrderBy`: converts the first entry of a GraphQL v2
 * OrderBy array back to a UI order string (e.g., for a `BAITable`'s `order`
 * prop, or to persist the current sort to the URL).
 *
 * @param orderBy - An OrderBy array (or its first entry's `field`/`direction`).
 * @returns The order string, or `null` if `orderBy` is empty/undefined.
 *
 * @example
 * convertFirstOrderByToString([{ field: 'NAME', direction: 'ASC' }])
 * // => 'name'
 *
 * @example
 * convertFirstOrderByToString([{ field: 'CREATED_AT', direction: 'DESC' }])
 * // => '-createdAt'
 */
export const convertFirstOrderByToString = (
  orderBy:
    | ReadonlyArray<{ field?: string; direction?: string }>
    | null
    | undefined,
): string | null => {
  const first = orderBy?.[0];
  if (!first?.field) return null;
  return `${first.direction === 'DESC' ? '-' : ''}${_.camelCase(first.field)}`;
};
